import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { ContactForm } from './ContactForm';
import { useHaloContactSubmission } from './useHaloContactSubmission';
import type { ContactFormMode } from './types';
import * as spokeStyles from './contactFormSpoke.styles';
import * as formStyles from './contactForm.styles';

type ContactFormSpokeProps = {
  onClose: () => void;
  listing?: ListingDetails;
  mode?: ContactFormMode;
};

export const ContactFormSpoke = ({ onClose, listing: listingProp, mode = 'contact' }: ContactFormSpokeProps) => {
  const reduxListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const user = useSelector((state: RootReduxState) => state.user);
  const userInquiryDataCache = useSelector((state: RootReduxState) => state.user?.inquiryDataCache);

  const listing = listingProp || reduxListing;

  if (!listing) {
    return null;
  }

  return (
    <ContactFormSpokeInner
      listing={listing}
      user={user}
      userInquiryDataCache={userInquiryDataCache}
      onClose={onClose}
      mode={mode}
    />
  );
};

type ContactFormSpokeInnerProps = {
  listing: ListingDetails;
  user: RootReduxState['user'];
  userInquiryDataCache: RootReduxState['user']['inquiryDataCache'];
  onClose: () => void;
  mode: ContactFormMode;
};

const ContactFormSpokeInner = ({ listing, user, userInquiryDataCache, onClose, mode }: ContactFormSpokeInnerProps) => {
  const { handleSubmitForm, isLoading, serverErrors } = useHaloContactSubmission({
    listing,
    onClose,
    requestToApply: mode === 'requestToApply',
  });
  const photos = listing?.largePhotos?.length ? listing.largePhotos : listing?.photos || [];
  const photoUrl = photos[0]?.url || listing?.previewPhoto?.url;

  return (
    <div className={spokeStyles.spokeFormWrapper}>
      {photoUrl && (
        <img src={photoUrl} alt={listing?.displayName || 'Listing photo'} className={formStyles.listingPhoto} />
      )}
      {listing?.displayName && <span className={formStyles.listingName}>{listing.displayName}</span>}
      <div className={spokeStyles.spokeFormContent}>
        <ContactForm
          listing={listing}
          onSubmit={handleSubmitForm}
          isLoading={isLoading}
          serverErrors={serverErrors}
          userInquiryDataCache={userInquiryDataCache}
          user={user}
          mode={mode}
        />
      </div>
      {isLoading && (
        <div className={spokeStyles.loadingOverlay} aria-hidden="true">
          <div className={spokeStyles.loadingSpinner} />
        </div>
      )}
    </div>
  );
};
